/**
 * The day in five tiles above the containers (20/10/2026 — the same tiles
 * as the country header, so Live reads like the rest): drops sent of
 * planned, SMS and the estimated spend, the next drop and when, the last
 * send and how long ago, and what needs a look. Every number comes from
 * today's real drops (live-totals).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { AlertTriangle, CheckCircle2, Clock, MessageSquare, Send } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { DayPlanContainerRow } from '../../../drop-generator.types';
import { instantText } from '../../../utils/clock/dk-label';
import { FactTile } from '../../board-header/schedule/FactTile';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { liveTotals } from './live-totals';
import { costWords, dropsStatus, lastWords, lookWords, nextWords } from './summary-words';

const GRID = 'grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60 shadow-sm lg:grid-cols-5 [&>*:last-child:nth-child(odd)]:col-span-2 lg:[&>*:last-child:nth-child(odd)]:col-span-1';
const n = (x: number) => x.toLocaleString('en-US');

export const LiveSummary: FC<{ containers: DayPlanContainerRow[]; now: Date }> = ({ containers, now }) => {
  const t = liveTotals(containers, now);
  const clock = useCountryClock();
  const drops = dropsStatus(t);
  const next = nextWords(t, now);
  const last = lastWords(t, now);
  const look = lookWords(t);
  return (
    <div className={GRID} data-testid="live-summary">
      <FactTile icon={Send} label="Drops" detail={`${n(t.drops.planned)} planned today`} status={drops} testId="live-drops">
        {n(t.drops.sent)} <span className="font-normal text-muted-foreground">/ {n(t.drops.planned)}</span>
      </FactTile>
      <FactTile icon={MessageSquare} label="SMS" detail={costWords(t) ?? 'no price known'} status={{ text: 'sent of planned', tone: 'muted' }} testId="live-sms">
        {n(t.sms.sent)} <span className="font-normal text-muted-foreground">/ {n(t.sms.planned)}</span>
      </FactTile>
      <FactTile icon={Clock} label="Next drop" detail={t.next ? t.next.container : ' '} status={next} testId="live-next">
        {t.next ? instantText(t.next.atUtc, clock) : '—'}
      </FactTile>
      <FactTile icon={CheckCircle2} label="Last sent" detail={t.last ? t.last.container : 'nothing yet'} status={{ text: last ?? 'waiting for the first send', tone: last ? 'success' : 'muted' }} testId="live-last">
        {t.last ? instantText(t.last.atUtc, clock) : '—'}
      </FactTile>
      <FactTile icon={AlertTriangle} label="Needs a look" detail={look.count > 0 ? 'open the rows below' : ' '} status={look} testId="live-look">
        <span className={cn(look.count > 0 && 'text-warning')}>{n(look.count)}</span>
      </FactTile>
    </div>
  );
};
